/**
 * Outstanding setup for one module, shown at the top of that module's dashboard.
 *
 * Spec: .claude/specs/module-setup-cards.md
 *
 * Deliberately NOT the Core onboarding banner. That one dismisses permanently and
 * is hidden on every module page, so a module bought after onboarding finished got
 * a checklist item nobody could ever see. This card lives inside the module it
 * describes — where the person who just bought it actually is.
 *
 * Renders nothing once every item is done, and nothing while loading, so a
 * completed dashboard never flashes a card on its way to steady state. There is no
 * dismiss control: every item is genuinely completable, so completion is the only
 * exit needed.
 */
import React from "react";
import { Link } from "react-router-dom";

import { getModuleSetup } from "../api";
import { useQuery } from "@tanstack/react-query";
import { ModuleKey, MODULE_COLORS, MODULE_LABELS } from "../moduleMetadata";

export interface ModuleSetupItem {
  id: string;
  label: string;
  done: boolean;
  link: string;
  hint?: string;
}

export interface ModuleSetupData {
  module: string;
  items: ModuleSetupItem[];
  done_count: number;
  total: number;
  complete: boolean;
}

export default function ModuleSetupCard({ module }: { module: ModuleKey }) {
  const { data } = useQuery<ModuleSetupData>({
    queryKey: ["module-setup", module],
    queryFn: () => getModuleSetup(module),
    staleTime: 60 * 1000,
    // A non-admin gets 403 here by design — the settings pages every item links to
    // are admin-only. Failing quietly leaves the dashboard intact for them.
    retry: false,
  });

  if (!data || data.complete || data.total === 0) return null;

  const accent = MODULE_COLORS[module];
  const remaining = data.items.filter(i => !i.done);

  return (
    <section
      aria-labelledby={`setup-${module}-heading`}
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderLeft: `4px solid ${accent}`,
        borderRadius: 10,
        padding: "18px 22px",
        marginBottom: 24,
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      }}
    >
      <div style={{
        display: "flex", alignItems: "baseline", justifyContent: "space-between",
        gap: 12, flexWrap: "wrap", marginBottom: 12,
      }}>
        <h2 id={`setup-${module}-heading`} style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
          Finish setting up {MODULE_LABELS[module]}
        </h2>
        <span style={{ fontSize: 12.5, color: "#64748b", fontVariantNumeric: "tabular-nums" }}>
          {data.done_count} of {data.total} done
        </span>
      </div>

      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexWrap: "wrap", gap: 8 }}>
        {remaining.map(item => (
          <li key={item.id}>
            <Link
              to={item.link}
              title={item.hint}
              style={{
                display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1,
                background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10,
                padding: "6px 13px", color: "#0f172a", fontWeight: 600,
                textDecoration: "none", fontSize: 12.5,
              }}
            >
              <span>{item.label} →</span>
              {item.hint && (
                <span style={{ fontWeight: 400, fontSize: 11, color: "#64748b" }}>{item.hint}</span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
